import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import {
  categoriesQuery,
  settingsQuery,
  banksQuery,
  subcategoriesQuery,
  orgsQuery,
} from "@/project/lib/queries";
import {
  inr,
  isValidAadhaar,
  isValidIfsc,
  isValidMobile,
  STATUSES,
} from "@/project/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type AppFormValues = {
  application_date: string;
  ration_card_no: string;
  applicant_name: string;
  applicant_address: string;
  applicant_mobile: string;
  applicant_alt_mobile: string;
  applicant_aadhaar: string;
  same_as_applicant: boolean;
  beneficiary_name: string;
  beneficiary_relation: string;
  beneficiary_mobile: string;
  beneficiary_aadhaar: string;
  category_id: string;
  subcategory: string;
  purpose: string;
  total_requirement: string;
  applicant_contribution: string;
  our_contribution: string;
  other_received: string;
  bank_account_holder: string;
  bank_name: string;
  bank_branch: string;
  bank_account_no: string;
  bank_ifsc: string;
  bank_account_type: string;
  disbursed_bank: string;
  issuing_organization: string;
  passbook_uploaded: boolean;
  remarks: string;
  status: string;
};

export const emptyApplication = (rationCard = ""): AppFormValues => ({
  application_date: new Date().toISOString().slice(0, 10),
  ration_card_no: rationCard,
  applicant_name: "",
  applicant_address: "",
  applicant_mobile: "",
  applicant_alt_mobile: "",
  applicant_aadhaar: "",
  same_as_applicant: false,
  beneficiary_name: "",
  beneficiary_relation: "",
  beneficiary_mobile: "",
  beneficiary_aadhaar: "",
  category_id: "",
  subcategory: "",
  purpose: "",
  total_requirement: "",
  applicant_contribution: "",
  our_contribution: "",
  other_received: "",
  bank_account_holder: "",
  bank_name: "",
  bank_branch: "",
  bank_account_no: "",
  bank_ifsc: "",
  bank_account_type: "Savings",
  disbursed_bank: "",
  issuing_organization: "",
  passbook_uploaded: false,
  remarks: "",
  status: "Draft",
});

export function num(v: string | number | null | undefined) {
  const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
  return isFinite(n) ? n : 0;
}

export function validateApplication(v: AppFormValues): string | null {
  if (!v.ration_card_no.trim()) {
    return "Ration Card Number is required";
  }
  if (!v.applicant_name.trim()) {
    return "Applicant Name is required";
  }
  if (!v.issuing_organization.trim()) {
    return "Company is required";
  }
  if (!v.category_id) {
    return "Assistance Category is required";
  }
  if (num(v.total_requirement) <= 0) {
    return "Total Requirement must be greater than zero";
  }
  if (v.applicant_mobile && !isValidMobile(v.applicant_mobile)) {
    return "Mobile number must be 10 digits starting with 6-9";
  }
  if (v.applicant_aadhaar && !isValidAadhaar(v.applicant_aadhaar)) {
    return "Aadhaar Number must be 12 digits";
  }
  if (v.bank_ifsc && !isValidIfsc(v.bank_ifsc)) {
    return "IFSC code format is invalid";
  }
  const contributions =
    num(v.applicant_contribution) +
    num(v.our_contribution) +
    num(v.other_received);
  if (contributions > num(v.total_requirement)) {
    return "Contributions cannot exceed the Total Requirement";
  }
  return null;
}

export function toRow(v: AppFormValues, userId?: string) {
  return {
    application_date: v.application_date,
    ration_card_no: v.ration_card_no.trim().toUpperCase(),
    applicant_name: v.applicant_name.trim(),
    applicant_address: v.applicant_address || null,
    applicant_mobile: v.applicant_mobile || null,
    applicant_alt_mobile: v.applicant_alt_mobile || null,
    applicant_aadhaar: v.applicant_aadhaar || null,
    same_as_applicant: v.same_as_applicant,
    beneficiary_name:
      (v.same_as_applicant ? v.applicant_name : v.beneficiary_name) || null,
    beneficiary_relation: v.same_as_applicant
      ? "Self"
      : v.beneficiary_relation || null,
    beneficiary_mobile:
      (v.same_as_applicant ? v.applicant_mobile : v.beneficiary_mobile) || null,
    beneficiary_aadhaar:
      (v.same_as_applicant ? v.applicant_aadhaar : v.beneficiary_aadhaar) ||
      null,
    category_id: v.category_id,
    subcategory: v.subcategory || null,
    purpose: v.purpose || null,
    total_requirement: num(v.total_requirement),
    applicant_contribution: num(v.applicant_contribution),
    our_contribution: num(v.our_contribution),
    other_received: num(v.other_received),
    bank_account_holder: v.bank_account_holder || null,
    bank_name: v.bank_name || null,
    bank_branch: v.bank_branch || null,
    bank_account_no: v.bank_account_no || null,
    bank_ifsc: v.bank_ifsc ? v.bank_ifsc.toUpperCase() : null,
    bank_account_type: v.bank_account_type || null,
    disbursed_bank: v.disbursed_bank || null,
    issuing_organization: v.issuing_organization || null,
    passbook_uploaded: v.passbook_uploaded,
    remarks: v.remarks || null,
    status: v.status,
    updated_by: userId ?? null,
  };
}

function Field({
  label,
  children,
  hint,
  error,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  error?: string | null;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function ApplicationForm({
  value,
  onChange,
}: {
  value: AppFormValues;
  onChange: (v: AppFormValues) => void;
}) {
  const { data: categories } = useQuery(categoriesQuery);
  const { data: banks } = useQuery(banksQuery);
  const { data: subcategories } = useQuery(subcategoriesQuery);
  const { data: settings } = useQuery(settingsQuery);
  const { data: companies } = useQuery(orgsQuery);
  const { data: allBanks } = useQuery(banksQuery);
  const disbursedBank = useMemo(
    () => (allBanks ?? []).find((b: any) => b.name === value.disbursed_bank) ?? null,
    [allBanks, value.disbursed_bank]
  );
  const companyOptions = useMemo(() => {
    const names = (companies ?? []).map((c: { name: string }) => c.name);
    const current = value.issuing_organization;
    return current && !names.includes(current) ? [current, ...names] : names;
  }, [companies, value.issuing_organization]);
  const set = <K extends keyof AppFormValues>(k: K, v: AppFormValues[K]) =>
    onChange({ ...value, [k]: v });
  const [bankTouched, setBankTouched] = useState(false);

  useEffect(() => {
    if (!bankTouched && !value.bank_name && settings?.["default_bank_name"]) {
      onChange({ ...value, bank_name: settings["default_bank_name"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.["default_bank_name"]]);

  const balance = useMemo(
    () =>
      Math.max(
        num(value.total_requirement) -
          num(value.applicant_contribution) -
          num(value.our_contribution) -
          num(value.other_received),
        0
      ),
    [value]
  );

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Applicant Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Application Date">
            <Input
              type="date"
              value={value.application_date}
              onChange={e => set("application_date", e.target.value)}
            />
          </Field>
          <Field label="Company *" hint="From Company Master">
            <Select
              value={value.issuing_organization}
              onValueChange={v => set("issuing_organization", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companyOptions.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field
            label="Ration Card No."
            hint="Optional — auto-generated if blank"
          >
            <Input
              placeholder="RC-2026-0001"
              value={value.ration_card_no}
              onChange={e =>
                set("ration_card_no", e.target.value.toUpperCase())
              }
            />
          </Field>
          <Field label="Applicant Name *">
            <Input
              placeholder="Full name as per records"
              value={value.applicant_name}
              onChange={e => set("applicant_name", e.target.value)}
            />
          </Field>
          <Field label="Applicant Address">
            <Input
              placeholder="Building, street, area"
              value={value.applicant_address}
              onChange={e => set("applicant_address", e.target.value)}
            />
          </Field>
          <Field
            label="Mobile Number *"
            error={
              value.applicant_mobile && !isValidMobile(value.applicant_mobile)
                ? "Enter a valid 10-digit mobile number"
                : null
            }
          >
            <Input
              inputMode="numeric"
              maxLength={15}
              placeholder="10-digit mobile number"
              value={value.applicant_mobile}
              onChange={e => set("applicant_mobile", e.target.value)}
            />
          </Field>
          <Field label="Alternate Mobile">
            <Input
              inputMode="numeric"
              maxLength={15}
              placeholder="Optional"
              value={value.applicant_alt_mobile}
              onChange={e => set("applicant_alt_mobile", e.target.value)}
            />
          </Field>
          <Field
            label="Aadhaar Number"
            hint="12 digits; masked on list screens"
            error={
              value.applicant_aadhaar &&
              !isValidAadhaar(value.applicant_aadhaar)
                ? "Aadhaar must be 12 digits"
                : null
            }
          >
            <Input
              inputMode="numeric"
              maxLength={14}
              placeholder="XXXX XXXX 1234"
              value={value.applicant_aadhaar}
              onChange={e => set("applicant_aadhaar", e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-4 space-y-0 pb-3">
          <CardTitle className="text-base">Beneficiary Details</CardTitle>
          <label className="flex items-center gap-2 text-sm font-normal">
            <Checkbox
              checked={value.same_as_applicant}
              onCheckedChange={c => set("same_as_applicant", Boolean(c))}
            />
            Applicant and Beneficiary are same
          </label>
        </CardHeader>
        {!value.same_as_applicant && (
          <CardContent className="grid gap-4 md:grid-cols-4">
            <Field label="Beneficiary Name">
              <Input
                placeholder="Person receiving assistance"
                value={value.beneficiary_name}
                onChange={e => set("beneficiary_name", e.target.value)}
              />
            </Field>
            <Field label="Relationship">
              <Input
                placeholder="Son / Wife / Mother"
                value={value.beneficiary_relation}
                onChange={e => set("beneficiary_relation", e.target.value)}
              />
            </Field>
            <Field label="Beneficiary Mobile">
              <Input
                inputMode="numeric"
                maxLength={15}
                placeholder="Optional"
                value={value.beneficiary_mobile}
                onChange={e => set("beneficiary_mobile", e.target.value)}
              />
            </Field>
            <Field
              label="Beneficiary Aadhaar"
              error={
                value.beneficiary_aadhaar &&
                !isValidAadhaar(value.beneficiary_aadhaar)
                  ? "Aadhaar must be 12 digits"
                  : null
              }
            >
              <Input
                inputMode="numeric"
                maxLength={14}
                placeholder="12-digit number"
                value={value.beneficiary_aadhaar}
                onChange={e => set("beneficiary_aadhaar", e.target.value)}
              />
            </Field>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Assistance &amp; Financial Requirement
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Assistance Category *">
            <Select
              value={value.category_id}
              onValueChange={v => set("category_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {(categories ?? [])
                  .filter(c => c.active)
                  .map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Subcategory">
            {(() => {
              const options = (subcategories ?? []).filter(
                sc => sc.active && sc.category_id === value.category_id
              );
              return options.length ? (
                <Select
                  value={value.subcategory}
                  onValueChange={v => set("subcategory", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map(sc => (
                      <SelectItem key={sc.id} value={sc.name}>
                        {sc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="e.g. Surgery, School fees"
                  value={value.subcategory}
                  onChange={e => set("subcategory", e.target.value)}
                />
              );
            })()}
          </Field>
          <Field label="Status">
            <Select value={value.status} onValueChange={v => set("status", v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-3">
            <Field label="Purpose / Reason">
              <Textarea
                rows={2}
                placeholder="Brief reason for the assistance, as it should read on the letter"
                value={value.purpose}
                onChange={e => set("purpose", e.target.value)}
              />
            </Field>
          </div>
          <Field label="A. Total Requirement (₹) *">
            <Input
              inputMode="numeric"
              placeholder="0"
              value={value.total_requirement}
              onChange={e => set("total_requirement", e.target.value)}
            />
          </Field>
          <Field label="B. Applicant / Own Contribution (₹)">
            <Input
              inputMode="numeric"
              placeholder="0"
              value={value.applicant_contribution}
              onChange={e => set("applicant_contribution", e.target.value)}
            />
          </Field>
          <Field label="C. Our Contribution (₹)">
            <Input
              inputMode="numeric"
              placeholder="0"
              value={value.our_contribution}
              onChange={e => set("our_contribution", e.target.value)}
            />
          </Field>
          <Field label="D. Other Already Received (₹)" hint="E = A − B − C − D">
            <Input
              inputMode="numeric"
              placeholder="0"
              value={value.other_received}
              onChange={e => set("other_received", e.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <div className="flex h-full flex-col justify-center rounded-lg bg-primary px-5 py-4 text-primary-foreground">
              <p className="text-xs uppercase tracking-wide opacity-80">
                E. Balance Required Till Today
              </p>
              <p className="font-display text-3xl font-semibold">
                {inr(balance)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Bank Account Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <Field label="Account Holder Name">
            <Input
              placeholder="As printed on the passbook"
              value={value.bank_account_holder}
              onChange={e => set("bank_account_holder", e.target.value)}
            />
          </Field>
          <Field label="Bank Name">
            <Select
              value={value.bank_name}
              onValueChange={v => {
                setBankTouched(true);
                const bank = (banks ?? []).find((b: any) => b.name === v);
                onChange({
                  ...value,
                  bank_name: v,
                  bank_branch: value.bank_branch ?? bank?.branch ?? "",
                  bank_ifsc: value.bank_ifsc ?? bank?.ifsc ?? "",
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select bank" />
              </SelectTrigger>
              <SelectContent>
                {(banks ?? [])
                  .filter((b: any) => b.active)
                  .map((b: any) => (
                    <SelectItem key={b.id} value={b.name}>
                      {b.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
          <Field
            label="Payment Disbursed From Bank (Organisation)"
            hint="Sanstha ka bank jisme se applicant ko paisa diya jayega"
          >
            <Select
              value={value.disbursed_bank}
              onValueChange={v => set("disbursed_bank", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select bank that paid" />
              </SelectTrigger>
              <SelectContent>
                {(banks ?? [])
                  .filter((b: any) => b.active)
                  .map((b: any) => (
                    <SelectItem key={b.id} value={b.name}>
                      {b.name}
                      {b.branch ? ` — ${b.branch}` : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
          {disbursedBank && (
            <div className="bg-muted/40 rounded-md border p-3 text-sm md:col-span-3">
              <div className="mb-1 font-medium">
                Organisation Bank Details — {disbursedBank.name}
              </div>
              <div className="grid gap-2 text-muted-foreground md:grid-cols-3">
                <div>Branch: {disbursedBank.branch ?? "—"}</div>
                <div>IFSC: {disbursedBank.ifsc ?? "—"}</div>
                <div>City: {disbursedBank.city ?? "—"}</div>
              </div>
            </div>
          )}
          <Field label="Branch">
            <Input
              placeholder="Branch name"
              value={value.bank_branch}
              onChange={e => set("bank_branch", e.target.value)}
            />
          </Field>
          <Field label="Account Number">
            <Input
              inputMode="numeric"
              placeholder="Account number"
              value={value.bank_account_no}
              onChange={e => set("bank_account_no", e.target.value)}
            />
          </Field>
          <Field
            label="IFSC Code"
            error={
              value.bank_ifsc && !isValidIfsc(value.bank_ifsc)
                ? "Invalid IFSC code"
                : null
            }
          >
            <Input
              maxLength={11}
              placeholder="BARB0XXXXXX"
              value={value.bank_ifsc}
              onChange={e => set("bank_ifsc", e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Account Type">
            <Select
              value={value.bank_account_type || "Savings"}
              onValueChange={v => set("bank_account_type", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["Savings", "Current", "Jan Dhan"].map(t => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <label className="flex items-end gap-2 pb-2 text-sm">
            <Checkbox
              checked={value.passbook_uploaded}
              onCheckedChange={c => set("passbook_uploaded", Boolean(c))}
            />
            Passbook copy submitted
          </label>
          <div className="md:col-span-3">
            <Field label="Remarks">
              <Textarea
                rows={2}
                placeholder="Internal notes (not printed on the letter)"
                value={value.remarks}
                onChange={e => set("remarks", e.target.value)}
              />
            </Field>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export async function ensureFamily(
  rationCard: string,
  address: string,
  userId?: string
) {
  const rc = rationCard.trim().toUpperCase();
  const { data: existing } = await supabase
    .from("families")
    .select("id")
    .eq("ration_card_no", rc)
    .maybeSingle();
  if (existing) {
    return existing.id;
  }
  const { data, error } = await supabase
    .from("families")
    .insert({
      ration_card_no: rc,
      address: address || null,
      created_by: userId ?? null,
    })
    .select("id")
    .single();
  if (error) {
    toast.error(error.message);
    throw error;
  }
  return data.id;
}
